import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, Phone, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const primaryAreas = [
  { city: 'Los Angeles', description: 'Downtown LA, Hollywood, Echo Park, Silver Lake, and all surrounding neighborhoods' },
  { city: 'Santa Monica', description: 'Beach communities, Main Street, and Montana Avenue areas' },
  { city: 'Beverly Hills', description: 'Luxury residential areas and commercial properties' },
  { city: 'Pasadena', description: 'Old Town, South Pasadena, and surrounding communities' },
];

const additionalAreas = [
  'Glendale',
  'Burbank',
  'Long Beach',
  'Torrance',
  'Culver City',
  'West Hollywood',
  'Malibu',
  'Hermosa Beach',
  'Manhattan Beach',
  'Redondo Beach',
  'El Segundo',
  'Inglewood',
  'Hawthorne',
  'Gardena',
  'Carson',
  'Compton',
  'Downey',
  'Norwalk',
  'Whittier',
  'La Mirada',
  'Cerritos',
  'Lakewood',
  'Bellflower',
  'Paramount',
];

export const ServiceAreasPage = () => {
  return (
    <main className="pt-20" data-testid="service-areas-page">
      {/* Page Header */}
      <section className="page-header py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
              Service Areas
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-amber-950 font-serif mb-6">
              Serving Los Angeles & Beyond
            </h1>
            <p className="text-lg text-stone-600 leading-relaxed">
              Buy Fusion provides professional flooring installation services throughout Los Angeles County and surrounding areas. Find out if we serve your neighborhood.
            </p>
          </div>
        </div>
      </section>

      {/* Primary Service Areas */}
      <section className="py-20 lg:py-28 section-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
              Primary Coverage
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-amber-950 font-serif mb-4">
              Main Service Areas
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              We have the strongest presence in these key Los Angeles areas, with quick response times and dedicated local teams.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {primaryAreas.map((area, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border border-stone-100 shadow-lg hover:shadow-xl transition-shadow"
                data-testid={`primary-area-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-7 h-7 text-green-700" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-amber-950 font-serif mb-2">{area.city}</h3>
                    <p className="text-stone-600">{area.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Areas */}
      <section className="py-20 lg:py-28 section-stone noise-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
              Extended Coverage
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-amber-950 font-serif mb-4">
              Additional Service Areas
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              We also serve these communities throughout Los Angeles County and beyond.
            </p>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-stone-100">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {additionalAreas.map((area, index) => (
                <div key={index} className="flex items-center gap-2" data-testid={`additional-area-${index}`}>
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span className="text-stone-700 text-sm">{area}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-stone-600 mb-4">Don't see your area listed?</p>
            <Link to="/contact">
              <Button
                variant="outline"
                className="rounded-full px-8 py-6 text-lg font-medium border-2 border-stone-300 hover:border-amber-950 hover:bg-amber-950 hover:text-white transition-all"
                data-testid="area-contact-btn"
              >
                Contact Us to Check Availability
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Location Matters */}
      <section className="py-20 lg:py-28 section-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
                Local Expertise
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-amber-950 font-serif mb-6">
                Why Local Service Matters
              </h2>
              <p className="text-stone-600 text-lg leading-relaxed mb-6">
                As a locally owned and operated flooring company, we understand the unique needs of Southern California homes and businesses. From dealing with coastal humidity in beach communities to meeting the style demands of luxury Beverly Hills properties, our local expertise ensures the best results for your project.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Fast response times for estimates and service',
                  'Knowledge of local building codes and requirements',
                  'Relationships with local material suppliers',
                  'Understanding of regional climate considerations',
                  'Active involvement in our community',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-stone-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-stone-100 rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center">
                  <MapPin className="w-7 h-7 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-amber-950 font-serif">Our Headquarters</h3>
                  <p className="text-stone-600">Visit our showroom</p>
                </div>
              </div>
              <address className="not-italic text-stone-700 mb-6">
                <strong>Buy Fusion - Premium Flooring Co.</strong><br />
                88 Sunset Boulevard<br />
                Los Angeles, CA 800134
              </address>
              <div className="space-y-2 text-stone-600">
                <p><strong>Phone:</strong> <a href="tel:+18888791244" className="text-amber-600 hover:text-amber-700">+1-888-879-1244</a></p>
                <p><strong>Email:</strong> <a href="mailto:support@buyfusion.store" className="text-amber-600 hover:text-amber-700">support@buyfusion.store</a></p>
              </div>
              <div className="mt-6 pt-6 border-t border-stone-200">
                <p className="font-semibold text-amber-950 mb-2">Business Hours</p>
                <div className="text-stone-600 text-sm space-y-1">
                  <p>Mon-Fri: 8:00 AM - 6:00 PM</p>
                  <p>Sat: 9:00 AM - 4:00 PM</p>
                  <p>Sun: Closed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-amber-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white font-serif mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-amber-100 text-lg mb-8 max-w-2xl mx-auto">
            Whether you're in downtown LA or the South Bay, our team is ready to help with your flooring project. Contact us today for a free estimate.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                className="bg-white text-amber-950 hover:bg-stone-100 rounded-full px-8 py-6 font-semibold"
                data-testid="areas-page-cta-estimate"
              >
                Get Free Estimate
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+18888791244">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-amber-950 rounded-full px-8 py-6 font-semibold"
                data-testid="areas-page-cta-call"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call +1-888-879-1244
              </Button>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServiceAreasPage;
